import React, { useState, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Flex, Text, View, Heading, TextArea, Button, IconButton, Alert, Link, ScreenReaderContent } from '@smarterservices/smarter-ui';
import { IconStarSolid, IconStarLightSolid, IconWarningSolid } from '@smarterservices/smarter-ui';
import { submitSurveyResponses } from '../../helpers/helpers';
import DualPane from '../DualPane';
import StepInstructions from '../Common/StepInstructions';
import RatingInput from './inputs/RatingInput';
import CommentInput from './inputs/CommentInput';
import SinglePane from '../SinglePane';

const Survey = ({ stepData, setIsTaskComplete, surveyData, onSubmit }) => {
    const [isSurveySubmitted, setIsSurveySubmitted] = useState(false);


  const { control, handleSubmit, formState: { isSubmitting, errors } } = useForm({
    defaultValues: {
      // Initialize with empty values for each question
      ...(surveyData?.results?.reduce((acc, q) => {
        acc[`question_${q.sid}`] = q.type === 'rating' ? 0 : '';
        return acc;
      }, {}) || {})
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const onSurveySubmitted = async (data, e) => {
    e?.preventDefault();
    
    try {
      // Group responses by question SID
      const responsesByQuestion = {};
      
      // Process all form fields
      Object.entries(data).forEach(([key, value]) => {
        // get the question sid, which is the second part of the key that is split by '_'
        const questionSid = key.split('_')[1];
        const question = surveyData?.results?.find(q => q.sid === questionSid);
        
        if(question.type === 'rating') {
          responsesByQuestion[questionSid] = { questionSid, rating: data[`question_${questionSid}_rating`],  comment: data[`question_${questionSid}_comment`] };
        } else if (question.type === 'comment') {
          responsesByQuestion[questionSid] = { questionSid, rating: null, comment: data[`question_${questionSid}_comment`] };
        }
      });
      
      // Convert to an array and prep for submission... 
      const submission = {
        responses: Object.values(responsesByQuestion)
          .filter(response => 'rating' in response) // Only include questions with a rating
          .map(({ questionSid, rating = null, comment = null }) => {

            const questionPayload = {
              questionSid,
              rating : Number(rating),
              comment
            };
            
            if(!comment && questionPayload.hasOwnProperty('comment')){
              delete questionPayload.comment;
            }
            
            return questionPayload;
          })
      };
      
      // TODO: Call your API to submit the survey
      const submitResult = await submitSurveyResponses({ payload: submission.responses });

      onSubmit(submitResult);      

    } catch (error) {
      console.error('Error submitting survey:', error);
      // You might want to show a toast or alert here for submission errors
    }
  };

  const RenderSurvey = () => (
    <View as="div" 
          className="session-survey-container" 
          margin="none">
      <form onSubmit={handleSubmit(onSurveySubmitted)}>
        <Heading level="h3" margin="0 0 medium 0">
          {stepData?.survey?.introduction || 'We value your feedback'}
        </Heading>
        
        <Flex as="div" 
              direction="column" 
              gap="medium">
          {surveyData?.results?.map((question, index) => {
            const fieldName = `question_${question.sid}`;
            const isRequired = question.required !== false; // Default to true if not specified
            const error = errors[fieldName];
            
            return (
              <View as="div" key={question.sid} padding="medium medium large medium" borderRadius="medium" borderColor='primary' borderWidth="small" background='secondary'>
                {question.type === 'rating' ? (
                  <RatingInput
                    name={fieldName}
                    control={control}
                    question={question}
                    required={isRequired}
                    error={error}
                  />
                ) : question.allowComments ? (
                  <CommentInput
                    name={fieldName}
                    control={control}
                    question={question}
                    required={isRequired}
                    error={error}
                  />
                ) : null}
              </View>
            );
          })}
          <View as="div" textAlign='center'>
            <Button
              type="submit"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : stepData?.survey?.button || 'Submit Feedback'}
            </Button>
          </View>
        </Flex>
      </form>
    </View>
  );

  return (
    <>
      {stepData && (
        <SinglePane>
        <Flex as="div" direction="column" alignItems="center" justifyContent="center" height="100%" margin="large none none none">
            <View 
              as="div" 
              className="session-survey-content"
              width="90%"
            >
            <RenderSurvey />
          </View>
        </Flex>
      </SinglePane>
      )}
    </>
  );
};

export default Survey;
